'use client'

import React, { useEffect, useState, useCallback } from "react";
import { useFormik } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '@/redux/store';
import { useGetLeanDetailsQuery, useCreateLeanDetailsMutation } from '@/redux/slices/preship/preship-api-slice';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { ChevronsLeft, ChevronsRight, Loader2, Info } from "lucide-react";
import { showSuccessToast, showErrorToast } from "../toast/toast";
import { Button } from "../ui/button";
import { useGetLeanConsolidatedDataQuery } from "@/redux/slices/preship/preship-api-slice";
import LeanConsolidatedView from "./LeanConsolidatedView";
import { useUpdateProjectTargetMutation } from "@/redux/slices/define/defineApiSlice";
import { setTarget } from "@/redux/slices/define/defineSlice";
import EditBanner from './EditBanner';
import { LeanService } from "@/services/LeanService";
interface LeanDetail {
  id?: number;
  palletLpnNumberId?: number;
  variant: string;
  weekType: string | null;
  sideABottom: string;
  sideATop: string;
  sideALean?: string;
  sideBBottom: string;
  sideBTop: string;
  sideBLean?: string;
  sideCBottom: string;
  sideCTop: string;
  sideCLean?: string;
  sideDBottom: string;
  sideDTop: string;
  sideDLean?: string;
}

interface PalletLpnNumber {
  id?: number;
  lpnNo: string;
  endStatusPallet: string;
  leanDetails: LeanDetail[];
}

interface FormValues {
  palletLpnNumbers: PalletLpnNumber[];
}

interface LeanProps {
  variant: 'preShip' | 'postShip' | 'postStack';
  week: string[];
  palletId?: number;
  isEditing?: boolean;
  onFormChange?: (hasChanges: boolean) => void;
  isOffline?: boolean;
  onOfflineSubmit?: (values: any) => Promise<void>;
  setIsLoading: (isLoading: boolean) => void;
}

const Lean: React.FC<LeanProps> = ({ variant, week, palletId, onFormChange, setIsLoading, isOffline, onOfflineSubmit, tabName, stage }) => {
  const { activePallet } = useSelector((state: RootState) => state.pallets);
  const { target, projectId } = useSelector((state: RootState) => state.define);
  const [showPalletNo, setShowPalletNo] = useState(true);
  const [showLpnNo, setShowLpnNo] = useState(true);
  const [showEndStatus, setShowEndStatus] = useState(true);
  const [isDataReady, setIsDataReady] = useState(false);
  const [isConsolidatedViewOpen, setIsConsolidatedViewOpen] = useState(false);
  const [initialValues, setInitialValues] = useState<FormValues | null>(null);
  const { data: leanData, isLoading, error, refetch } = useGetLeanDetailsQuery(
    { palletId: activePallet?.id, variant, test: activePallet?.numberOfPallets },
    { skip: !activePallet, refetchOnMountOrArgChange: true }
  );
  const [isEditing, setIsEditing] = useState(false);
  const [newTarget, setNewTarget] = useState<any>(2);
  const dispatch = useDispatch();
  const [createLeanDetails] = useCreateLeanDetailsMutation();
  const [updateProjectTarget, { isLoading: updateProjectLoading }] = useUpdateProjectTargetMutation();
  const [isAdvancedControlsOpen, setIsAdvancedControlsOpen] = useState(false);
  // const [isEditing, setIsEditing] = useState(false);
  const [offlineLeanData, setOfflineLeanData] = useState<any>(null);
  console.log(leanData, 'leanData')
  useEffect(() => {
    if (isOffline) {
      const getData = async () => {
        try {
          const res = await LeanService.getLeanDetails(Number(palletId), variant)
          setOfflineLeanData(res)
        } catch (e) {
          console.log('error in getData')
          console.log(e)
        }
      }
      getData()
    }
  }, [isOffline, activePallet])
  useEffect(() => {
    setNewTarget(target);
  }, [target]);
  useEffect(() => {
    return () => {
      if (isEditing) {
        setIsEditing(false)
      }
    }
  }, [isEditing])
  const initializePalletLpnNumbers = useCallback((numberOfPallets: number): PalletLpnNumber[] => {
    return Array.from({ length: numberOfPallets }, (): PalletLpnNumber => ({
      lpnNo: '',
      endStatusPallet: '',
      leanDetails: [{
        variant,
        weekType: null,
        sideABottom: '',
        sideATop: '',
        sideBBottom: '',
        sideBTop: '',
        sideCBottom: '',
        sideCTop: '',
        sideDBottom: '',
        sideDTop: '',
      }],
    }));
  }, [variant]);

  const formik = useFormik<FormValues>({
    initialValues: {
      palletLpnNumbers: [],
    },
    onSubmit: async (values) => {
      if (isOffline) {
        await LeanService.createLeanDetails({
          palletId: Number(palletId),
          palletLpnNumbers: values.palletLpnNumbers,

        });
        showSuccessToast("Successfully saved lean details!");
        return;
      }

      try {
        await createLeanDetails({
          palletId: activePallet?.id || 0,
          ...values
        }).unwrap();
        console.log({
          palletId: activePallet?.id || 0,
          ...values
        }, 'create lean')
        // refetch()
        showSuccessToast("Successfully saved lean details!");
      } catch (error) {
        showErrorToast("Failed to save lean details");
        console.error('Failed to save lean details:', error);
      }
    },
  });

  useEffect(() => {
    if (activePallet && !isLoading && !isOffline) {
      let initialPalletLpnNumbers: PalletLpnNumber[] = [];

      if (error && 'status' in error && error.status === 404) {
        initialPalletLpnNumbers = initializePalletLpnNumbers(Number(activePallet.numberOfPallets));
      } else if (leanData && leanData.palletLpnNumbers && leanData.palletLpnNumbers.length > 0) {
        initialPalletLpnNumbers = leanData.palletLpnNumbers.map((lpn: PalletLpnNumber) => ({
          ...lpn,
          endStatusPallet: lpn.endStatusPallet || '',
          leanDetails: lpn.leanDetails.length > 0 ? lpn.leanDetails : [{
            variant,
            weekType: null,
            sideABottom: '',
            sideATop: '',
            sideBBottom: '',
            sideBTop: '',
            sideCBottom: '',
            sideCTop: '',
            sideDBottom: '',
            sideDTop: '',
          }]
        }));
      }

      const currentNumberOfPallets = initialPalletLpnNumbers.length;
      const targetNumberOfPallets = Number(activePallet.numberOfPallets);

      if (currentNumberOfPallets < targetNumberOfPallets) {
        initialPalletLpnNumbers.push(...initializePalletLpnNumbers(targetNumberOfPallets - currentNumberOfPallets));
      } else if (currentNumberOfPallets > targetNumberOfPallets) {
        initialPalletLpnNumbers = initialPalletLpnNumbers.slice(0, targetNumberOfPallets);
      }

      const newValues = { palletLpnNumbers: initialPalletLpnNumbers };
      formik.setValues(newValues);
      setInitialValues(newValues);
      setIsDataReady(true);
    }
  }, [leanData, activePallet, activePallet?.numberOfPallets, error, variant, initializePalletLpnNumbers, isLoading]);

  useEffect(() => {
    if (activePallet) {
      refetch();
    }
  }, [activePallet?.numberOfPallets, refetch]);
  useEffect(() => {
    if (isOffline && offlineLeanData) {
      let initialPalletLpnNumbers: PalletLpnNumber[] = [];

      if (offlineLeanData && offlineLeanData.palletLpnNumbers && offlineLeanData.palletLpnNumbers.length > 0) {
        initialPalletLpnNumbers = offlineLeanData.palletLpnNumbers.map((lpn: PalletLpnNumber) => ({
          ...lpn,
          endStatusPallet: lpn.endStatusPallet || '',
          leanDetails: lpn.leanDetails.length > 0 ? lpn.leanDetails : [{
            variant,
            weekType: null,
            sideABottom: '',
            sideATop: '',
            sideBBottom: '',
            sideBTop: '',
            sideCBottom: '',
            sideCTop: '',
            sideDBottom: '',
            sideDTop: '',
          }]
        }));
      }

      const currentNumberOfPallets = initialPalletLpnNumbers.length;
      const targetNumberOfPallets = Number(activePallet?.numberOfPallets);

      if (currentNumberOfPallets < targetNumberOfPallets) {
        initialPalletLpnNumbers.push(...initializePalletLpnNumbers(targetNumberOfPallets - currentNumberOfPallets));
      } else if (currentNumberOfPallets > targetNumberOfPallets) {
        initialPalletLpnNumbers = initialPalletLpnNumbers.slice(0, targetNumberOfPallets);
      }

      const newValues = { palletLpnNumbers: initialPalletLpnNumbers };
      formik.setValues(newValues);
      setInitialValues(newValues);
      setIsDataReady(true);
    }
  }, [offlineLeanData, activePallet, isOffline]);
  console.log(offlineLeanData, 'offlineLeanData')
  useEffect(() => {
    if (activePallet) {
      refetch();
    }
  }, [activePallet?.numberOfPallets, refetch]);

  const calculateLean = useCallback((top: string, bottom: string) => {
    const topValue = parseFloat(top);
    const bottomValue = parseFloat(bottom);
    if (!isNaN(topValue) && !isNaN(bottomValue)) {
      return (bottomValue - topValue).toFixed(2);
    }
    return '';
  }, []);

  const updateTarget = async () => {
    try {
      const res = await updateProjectTarget({ target: Number(newTarget), projectId }).unwrap();
      console.log(res, 'res', 'target res')
      dispatch(setTarget(Number(newTarget)));
      showSuccessToast("Successfully updated!");
    } catch (e) {
      console.log(e);
      showErrorToast("Failed to update the target");
    }
  };

  const getLeanCellColor = useCallback((leanValue: string, target: number | string) => {
    const numericLean = parseFloat(leanValue);
    const numericTarget = Number(target);
    if (!isNaN(numericLean)) {
      if (numericLean >= -numericTarget && numericLean <= numericTarget) {
        return 'bg-states-success text-white';
      } else {
        return 'bg-bgRed text-white';
      }
    }
    return '';
  }, []);

  // Add keyboard navigation handler to move focus with arrow keys
  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLElement>, rowIndex: number, colIndex: number) => {
    let targetRow = rowIndex;
    let targetCol = colIndex;
    switch (e.key) {
      case 'ArrowUp':
        targetRow = Math.max(0, rowIndex - 1);
        break;
      case 'ArrowDown':
      case 'Enter': // Adding Enter key to behave like Down arrow - Excel behavior
        targetRow = Math.min(formik.values.palletLpnNumbers.length - 1, rowIndex + 1);
        break;
      case 'ArrowLeft':
        targetCol = Math.max(1, colIndex - 1);
        break;
      case 'ArrowRight':
        targetCol = colIndex + 1;
        break;
      default:
        return;
    }
    e.preventDefault(); // Prevent default, including form submission for Enter key
    const nextEl = document.querySelector(`[data-row="${targetRow}"][data-col="${targetCol}"]`) as HTMLElement | null;
    nextEl?.focus();
  }, [formik.values.palletLpnNumbers.length]);

  useEffect(() => {
    if (onFormChange && initialValues) {
      const hasChanges = JSON.stringify(formik.values) !== JSON.stringify(initialValues);
      onFormChange(hasChanges);
    }
  }, [formik.values, onFormChange, initialValues]);
  useEffect(() => {
    setIsLoading(formik.isSubmitting);
  }, [formik.isSubmitting, setIsLoading])

  // Add roundToNearestQuarter function
  const roundToNearestQuarter = (value: string): string => {
    if (!value || isNaN(Number(value))) return value;
    const num = parseFloat(value);
    const rounded = Math.round(num * 4) / 4;
    return rounded.toFixed(2);
  };


  const endStatusOptions = [{
    label: "Pre-Ship",
    value: "Pre Ship"
  }, {
    label: "Post-Ship",
    value: "Post Ship"
  },
  { label: "Stack start(stacked+30m)", value: "Stack start(stacked+30m)" },
  { label: "Stack Week-1", value: "Stack Week-1" },
  { label: "Stack Week-2", value: "Stack Week-2" },
  { label: "Stack Week-3", value: "Stack Week-3" },
  { label: "Stack Week-4 (unstacking+30M)", value: "Stack Week-4 (unstacking+30M)" }
  ]


  console.log(offlineLeanData, 'offlineLeanData')
  return (
    <div className="space-y-2 overflow-x-auto">
      {/* <EditBanner
        stage={variant}
        component="lean"
        palletId={palletId}
        onEditStateChange={setIsEditing}
      /> */}
      {!isOffline && <EditBanner

        stage={stage}
        palletId={palletId}
        component={tabName}
        onEditStateChange={setIsEditing}
      />}

      <form
        id={`lean-table-${activePallet?.id}`}
        className={''}
        onSubmit={formik.handleSubmit}
        onKeyDown={(e) => {
          // Prevent form submission when Enter is pressed inside the table
          if (e.key === 'Enter') {
            e.preventDefault();
          }
        }}
      >
        <Table className={`${(isEditing) || (isOffline) ? " " : "pointer-events-none"} border-collapse`}>
          <TableHeader>
            <TableRow>
              <TableHead className="border border-gray-300  text-blue p-0 px-3 text-[10px] md:text-sm font-semibold md:font-bold bg-light-blue text-left" colSpan={3}>
                Pallet Name: {activePallet?.name || 'N/A'} - Mode {activePallet?.shippingModes?.map((sp) => sp.mode).join(', ') || 'N/A'}
              </TableHead>
              <TableHead className="border border-gray-300 text-blue p-0 px-3 text-[10px] md:text-sm font-semibold md:font-bold bg-light-blue text-left" colSpan={12}>
                <div className='flex items-center gap-2 justify-between relative '>
                  <div>
                    <span className="text-xs flex items-center gap-2"><Info className="w-4 h-4" /> Note: Data will be rounded to the nearest 0.25 inch</span>

                    {isAdvancedControlsOpen && (
                      <div className="fixed inset-0 !pointer-events-auto bg-black bg-opacity-50 flex items-center justify-center z-50">
                        <div className="bg-white p-6 rounded-lg w-[500px]">
                          <h2 className="text-xl font-bold mb-4">Advanced Controls</h2>
                          <div className="space-y-4">
                            <div className="flex flex-col gap-2">
                              <label className=" text-base">Max Pallet Lean (Not greater than)</label>
                              <div className="flex items-center gap-4">
                                <input
                                  onChange={(e) => setNewTarget(e.target.value)}
                                  value={newTarget}
                                  type='number'
                                  className="border-2 border-blue px-2 py-1 text-base"
                                />
                              </div>
                            </div>
                            <div className="flex justify-end gap-2">

                              <Button
                                type='button'
                                onClick={() => setIsAdvancedControlsOpen(false)}
                                className="bg-gray-200 text-gray-800 hover:bg-bgRed hover:text-white"
                              >
                                Cancel
                              </Button>
                              <Button
                                type='button'
                                onClick={() => {
                                  updateTarget();
                                  setIsAdvancedControlsOpen(false);
                                }}
                                disabled={updateProjectLoading}
                                className="bg-blue text-white flex items-center gap-2"
                              >
                                {updateProjectLoading && <Loader2 className='animate-spin' />}
                                Update
                              </Button>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                  </div>
                  {/* { i want this div to be clickable even if the parent is not clickable } */}
          {!isOffline &&        <div onClick={(e) => e.stopPropagation()} className="  !pointer-events-auto   flex-center gap-4">

                    <Button
                      onClick={() => setIsAdvancedControlsOpen(true)}
                      type='button'
                      className="bg-white text-md font-semibold hover:bg-blue hover:text-white text-blue border-2 border-blue"
                    >
                      Advance Controls
                    </Button>
                    {/* <Button type='button' className="bg-white text-md font-semibold text-blue border-2 border-blue">
                      Clear All
                    </Button> */}
                    <Button
                      onClick={() => setIsConsolidatedViewOpen(true)}
                      type='button' className="bg-white text-md font-semibold hover:bg-blue hover:text-white text-blue border-2 border-blue">
                      Consolidated View
                    </Button>
                  </div>}
                </div>
              </TableHead>
            </TableRow>
            <TableRow>
              <TableHead className="border border-gray-300 text-black p-0 px-3 text-[10px] md:text-sm font-semibold md:font-bold bg-light-blue text-center" colSpan={3}>
                {variant === 'preShip' ? 'Pre-Ship' : variant === 'postShip' ? 'Post-Ship' : ""} pallet Lean
              </TableHead>
              {['Side A', 'Side B', 'Side C', 'Side D'].map((side) => (
                <TableHead key={side} className="border border-gray-300 text-black p-0 px-3 text-[10px] md:text-sm font-semibold md:font-bold bg-light-blue text-center" colSpan={3}>
                  {side}
                </TableHead>
              ))}
            </TableRow>
            <TableRow>
              <TableHead className={cn(
                "border border-gray-300 w-[150px] text-black p-0 text-left px-3 text-[10px] relative md:text-sm font-semibold md:font-bold bg-light-blue",
                !showPalletNo && "w-[20px] bg-blue"
              )}>
                {showPalletNo && "Pallet No"}
                <span
                  onClick={() => setShowPalletNo(!showPalletNo)}
                  className={cn(
                    "cursor-pointer !pointer-events-auto  hover:scale-125 text-white transition-all",
                    showPalletNo && "right-2 top-4 absolute"
                  )}
                >
                  {showPalletNo ? (
                    <ChevronsLeft size={20} className="text-blue" />
                  ) : (
                    <ChevronsRight size={20} className="text-white" />
                  )}
                </span>
              </TableHead>
              <TableHead className={cn(
                "border border-gray-300 w-[150px] text-black p-0 text-left px-3 text-[10px] relative md:text-sm font-semibold md:font-bold bg-light-blue",
                !showLpnNo && "w-[20px] bg-blue"
              )}>
                {showLpnNo && "Pallet Identifier"}
                <span
                  onClick={() => setShowLpnNo(!showLpnNo)}
                  className={cn(
                    "cursor-pointer !pointer-events-auto  hover:scale-125 text-white transition-all",
                    showLpnNo && "right-2 top-4 absolute"
                  )}
                >
                  {showLpnNo ? (
                    <ChevronsLeft size={20} className="text-blue" />
                  ) : (
                    <ChevronsRight size={20} className="text-white" />
                  )}
                </span>
              </TableHead>
              <TableHead className={cn(
                "border border-gray-300 w-[150px] text-black p-0 text-left px-3 text-[10px] relative md:text-sm font-semibold md:font-bold bg-light-blue",
                !showEndStatus && "w-[20px] bg-blue"
              )}>
                {showEndStatus && "End Status"}
                <span
                  onClick={() => setShowEndStatus(!showEndStatus)}
                  className={cn(
                    "cursor-pointer !pointer-events-auto  hover:scale-125 text-white transition-all",
                    showEndStatus && "right-2 top-4 absolute"
                  )}
                >
                  {showEndStatus ? (
                    <ChevronsLeft size={20} className="text-blue" />
                  ) : (
                    <ChevronsRight size={20} className="text-white" />
                  )}
                </span>
              </TableHead>
              {['Side A', 'Side B', 'Side C', 'Side D'].flatMap((side) =>
                ['Top (in)', 'Bottom (in)', 'Lean (in)'].map((header) => (
                  <TableHead
                    key={`${side}-${header}`}
                    className="border border-gray-300 text-black p-0 text-left px-3  text-[10px] md:text-sm font-semibold md:font-bold bg-light-blue"
                  >
                    {header}
                  </TableHead>
                ))
              )}
            </TableRow>
          </TableHeader>
          <TableBody>
            {formik.values.palletLpnNumbers.map((palletLpn, index) => {
              const isRowEditable = week.includes(palletLpn.endStatusPallet);
              return (
                <TableRow key={index} className={cn("w-2/3 bg-white")}>
                  <TableCell className="border border-gray-300 p-0 bg-states-disabled">
                    {showPalletNo && (
                      <Input
                        className="w-full  border-none "
                        value={index + 1}
                        readOnly
                      />
                    )}
                  </TableCell>
                  <TableCell className="border border-gray-300 p-0 bg-states-disabled">
                    {showLpnNo && (
                      <Input
                        className="w-full  border-none "
                        value={palletLpn.lpnNo}
                        readOnly
                      />
                    )}
                  </TableCell>
                  <TableCell className={cn("border border-gray-300 p-0", !showEndStatus && "bg-blue")}>
                    {showEndStatus && (
                      <Select
                        value={palletLpn.endStatusPallet || undefined}
                        onValueChange={(value) => {
                          formik.setFieldValue(`palletLpnNumbers.${index}.endStatusPallet`, value);
                        }}
                      >
                        <SelectTrigger
                          data-row={index} data-col={1}
                          onKeyDown={(e) => handleKeyDown(e, index, 1)}
                          className="w-full rounded-none bg-transparent border-2 border-required focus:border-blue"
                        >
                          <SelectValue placeholder="Select End Status" />
                        </SelectTrigger>
                        <SelectContent>
                          {endStatusOptions.map((status) => (
                            <SelectItem key={status.value} value={status.value}>
                              {status.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </TableCell>
                  {['A', 'B', 'C', 'D'].map((side, sideIndex) => (
                    <React.Fragment key={side}>
                      {['Top', 'Bottom', 'Lean'].map((position, posIndex) => {
                        const fieldName = `side${side}${position}` as keyof LeanDetail;
                        const isLeanField = position === 'Lean';
                        const bottomValue = palletLpn.leanDetails[0][`side${side}Bottom` as keyof LeanDetail] as string;
                        const topValue = palletLpn.leanDetails[0][`side${side}Top` as keyof LeanDetail] as string;
                        const leanValue = calculateLean(topValue, bottomValue);

                        return (
                          <TableCell
                            key={`${side}${position}`}
                            className={cn(
                              "border border-gray-300 p-0",
                              isLeanField && "bg-states-disabled",
                              isLeanField && getLeanCellColor(leanValue, target!)
                            )}
                          >
                            <Input
                              data-row={index} data-col={2 + sideIndex * 3 + posIndex}
                              onKeyDown={(e) => handleKeyDown(e, index, 2 + sideIndex * 3 + posIndex)}
                              className={cn("w-full h-9 border-none", !isRowEditable && "bg-gray-200")}
                              type={isLeanField ? "text" : "number"}
                              step="0.25"
                              placeholder={isLeanField ? "Value" : "Enter"}
                              value={isLeanField ? leanValue : palletLpn.leanDetails[0][fieldName] as string}
                              onChange={(e) => {
                                if (!isLeanField && isRowEditable) {
                                  // Allow free typing without rounding
                                  formik.setFieldValue(`palletLpnNumbers.${index}.leanDetails.0.${fieldName}`, e.target.value);
                                }
                              }}
                              onBlur={(e) => {
                                if (!isLeanField && isRowEditable && e.target.value) {
                                  // Only round when user finishes typing (on blur)
                                  const roundedValue = roundToNearestQuarter(e.target.value);
                                  formik.setFieldValue(`palletLpnNumbers.${index}.leanDetails.0.${fieldName}`, roundedValue);
                                }
                              }}
                              readOnly={isLeanField || !isRowEditable}
                              disabled={!isRowEditable}
                            />
                          </TableCell>
                        );
                      })}
                    </React.Fragment>
                  ))}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        <button type="submit" style={{ display: 'none' }}></button>
        {<LeanConsolidatedView
          isOpen={isConsolidatedViewOpen}
          onClose={() => setIsConsolidatedViewOpen(false)}
          variant={variant}
        />}
      </form>
    </div>
  );
};

export default Lean;
